import React, { useState } from 'react';
import './XPCalculator.css';

// Interfaces
interface Scenario {
  id: string;
  name: string;
  description: string;
}

interface MinionXPData {
  waveNumber: number;
  minionType: 'melee' | 'caster' | 'cannon';
  minionIndex: number; // Which minion in the wave (0-5 for normal, 0-6 for cannon)
  minionDescription: string; // e.g., "2nd Melee", "1st Caster", "Cannon"
  baseXP: number;
  soloXP: number;
  cumulativeXP: number;
  levelReached?: number; // If this minion kill causes a level up
  timing: string; // When this minion is killed
}

interface LevelData {
  level: number;
  xpRequired: number;
  cumulativeXP: number;
  soloWaves: number;
  sharedWaves: number;
}

interface Props {
  scenario: Scenario;
}

type KillOrder = 'melee-cannon-caster' | 'caster-cannon-melee' | 'melee-caster-cannon';
type LaneType = 'solo' | 'duo' | 'trio' | 'quad' | 'penta';

const XPCalculator: React.FC<Props> = ({ scenario }) => {
  const [selectedLevel, setSelectedLevel] = useState<number>(2);
  const [showAllLevels, setShowAllLevels] = useState<boolean>(false);
  const [killOrder, setKillOrder] = useState<KillOrder>('melee-cannon-caster');
  const [laneType, setLaneType] = useState<LaneType>('solo');

  // XP table data (levels 19-20 are Top Lane quest only)
  const levelData: LevelData[] = [
    { level: 1, xpRequired: 0, cumulativeXP: 0, soloWaves: 0, sharedWaves: 0 },
    { level: 2, xpRequired: 280, cumulativeXP: 280, soloWaves: 2, sharedWaves: 2 },
    { level: 3, xpRequired: 380, cumulativeXP: 660, soloWaves: 3, sharedWaves: 4 },
    { level: 4, xpRequired: 480, cumulativeXP: 1140, soloWaves: 4, sharedWaves: 6 },
    { level: 5, xpRequired: 580, cumulativeXP: 1720, soloWaves: 6, sharedWaves: 9 },
    { level: 6, xpRequired: 680, cumulativeXP: 2400, soloWaves: 9, sharedWaves: 12 },
    { level: 7, xpRequired: 780, cumulativeXP: 3180, soloWaves: 11, sharedWaves: 16 },
    { level: 8, xpRequired: 880, cumulativeXP: 4060, soloWaves: 14, sharedWaves: 21 },
    { level: 9, xpRequired: 980, cumulativeXP: 5040, soloWaves: 18, sharedWaves: 26 },
    { level: 10, xpRequired: 1080, cumulativeXP: 6120, soloWaves: 21, sharedWaves: 31 },
    { level: 11, xpRequired: 1180, cumulativeXP: 7300, soloWaves: 25, sharedWaves: 37 },
    { level: 12, xpRequired: 1280, cumulativeXP: 8580, soloWaves: 30, sharedWaves: 43 },
    { level: 13, xpRequired: 1380, cumulativeXP: 9960, soloWaves: 34, sharedWaves: 49 },
    { level: 14, xpRequired: 1480, cumulativeXP: 11440, soloWaves: 39, sharedWaves: 55 },
    { level: 15, xpRequired: 1580, cumulativeXP: 13020, soloWaves: 45, sharedWaves: 62 },
    { level: 16, xpRequired: 1680, cumulativeXP: 14700, soloWaves: 49, sharedWaves: 69 },
    { level: 17, xpRequired: 1780, cumulativeXP: 16480, soloWaves: 54, sharedWaves: 76 },
    { level: 18, xpRequired: 1880, cumulativeXP: 18360, soloWaves: 60, sharedWaves: 84 },
    { level: 19, xpRequired: 1980, cumulativeXP: 20340, soloWaves: 66, sharedWaves: 93 },  // Top Lane quest only
    { level: 20, xpRequired: 2080, cumulativeXP: 22420, soloWaves: 73, sharedWaves: 103 }   // Top Lane quest only
  ];

  // Base XP values (solo XP is 95% of base)
  const baseXPValues = {
    melee: 65,    // 62 solo XP ÷ 0.95
    caster: 32,   // 30 solo XP ÷ 0.95  
    cannon: 100   // 95 solo XP ÷ 0.95
  };

  const soloXPValues = {
    melee: 62,
    caster: 30,
    cannon: 95
  };

  // Calculate XP sharing percentage based on lane type
  const getXPSharingPercentage = (laneType: LaneType): number => {
    switch (laneType) {
      case 'solo': return 0.95; // 95% of base XP
      case 'duo': return 0.62; // 62% of base XP (124% ÷ 2)
      case 'trio': return 0.413; // 41.3% of base XP (123.9% ÷ 3)
      case 'quad': return 0.31; // 31% of base XP (124% ÷ 4)
      case 'penta': return 0.248; // 24.8% of base XP (124% ÷ 5)
      default: return 0.95;
    }
  };

  // Get actual XP values based on lane type
  const getActualXPValues = (laneType: LaneType) => {
    const sharingPercentage = getXPSharingPercentage(laneType);
    return {
      melee: Math.round(baseXPValues.melee * sharingPercentage),
      caster: Math.round(baseXPValues.caster * sharingPercentage),
      cannon: Math.round(baseXPValues.cannon * sharingPercentage)
    };
  };

  const actualXPValues = getActualXPValues(laneType);

  // ---------- Wave timing helpers (matches backend WaveGoldCalculator Season 2025) ----------
  // Wave intervals change at different game times:
  //   Phase 1 (waves 1-28):  30s intervals, first spawn at 0:30
  //   Phase 2 (waves 29-66): 25s intervals, from 14:00
  //   Phase 3 (waves 67+):   20s intervals, from 29:50
  const FIRST_WAVE_SPAWN = 30;     // 0:30 minions spawn
  const WAVE_TRAVEL_TIME = 30;     // 30s to reach lane
  const WAVE_CLEAR_TIME = 30;      // 30s to clear
  const WAVE_INTERVAL_EARLY = 30;  // waves 1-28
  const WAVE_INTERVAL_MID = 25;    // waves 29-66
  const WAVE_INTERVAL_LATE = 20;   // waves 67+
  const WAVE_AT_14_MIN = 28;
  const WAVE_AT_30_MIN = 66;

  const getWaveSpawnTime = (waveNumber: number): number => {
    if (waveNumber <= 0) return 0;
    if (waveNumber <= WAVE_AT_14_MIN) {
      return FIRST_WAVE_SPAWN + (waveNumber - 1) * WAVE_INTERVAL_EARLY;
    } else if (waveNumber <= WAVE_AT_30_MIN) {
      const baseTime = 840; // 14:00 in seconds
      return baseTime + (waveNumber - WAVE_AT_14_MIN) * WAVE_INTERVAL_MID;
    } else {
      const baseTime = 1790; // 29:50 in seconds
      return baseTime + (waveNumber - WAVE_AT_30_MIN) * WAVE_INTERVAL_LATE;
    }
  };

  const getWaveClearTime = (waveNumber: number): number => {
    return getWaveSpawnTime(waveNumber) + WAVE_TRAVEL_TIME + WAVE_CLEAR_TIME;
  };

  // Convert seconds to MM:SS format
  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  // Determine wave type based on wave number (Season 2025 cannon wave logic)
  const getWaveType = (waveNumber: number): 'normal' | 'cannon' => {
    if (waveNumber < 3) return 'normal';

    // Before 14:00 (waves 3-28): cannon every 3 waves starting at wave 3
    if (waveNumber <= WAVE_AT_14_MIN) {
      return (waveNumber % 3 === 0) ? 'cannon' : 'normal';
    }

    // After 25:00 (wave 55+): cannon every wave
    if (waveNumber >= 55) {
      return 'cannon';
    }

    // 14:00 to 25:00 (waves 29-54): cannon every 2 waves (odd waves)
    return (waveNumber % 2 === 1) ? 'cannon' : 'normal';
  };

  // Get minion kill order based on setting
  const getMinionKillOrder = (waveType: 'normal' | 'cannon'): ('melee' | 'caster' | 'cannon')[] => {
    const baseOrder = {
      'melee-cannon-caster': waveType === 'cannon' 
        ? ['melee', 'melee', 'melee', 'cannon', 'caster', 'caster', 'caster'] as const
        : ['melee', 'melee', 'melee', 'caster', 'caster', 'caster'] as const,
      'caster-cannon-melee': waveType === 'cannon'
        ? ['caster', 'caster', 'caster', 'cannon', 'melee', 'melee', 'melee'] as const
        : ['caster', 'caster', 'caster', 'melee', 'melee', 'melee'] as const,
      'melee-caster-cannon': waveType === 'cannon'
        ? ['melee', 'melee', 'melee', 'caster', 'caster', 'caster', 'cannon'] as const
        : ['melee', 'melee', 'melee', 'caster', 'caster', 'caster'] as const,
    };
    
    return [...baseOrder[killOrder]];
  };

  // Generate minion description based on type and count
  const getMinionDescription = (minionType: 'melee' | 'caster' | 'cannon', counts: {melee: number, caster: number, cannon: number}): string => {
    if (minionType === 'cannon') {
      return 'Cannon';
    } else if (minionType === 'melee') {
      counts.melee++;
      return counts.melee === 1 ? '1st Melee' : counts.melee === 2 ? '2nd Melee' : '3rd Melee';
    } else {
      counts.caster++;
      return counts.caster === 1 ? '1st Caster' : counts.caster === 2 ? '2nd Caster' : '3rd Caster';
    }
  };

  // Generate minion XP data for first 6 levels
  const generateMinionXPData = (): MinionXPData[] => {
    const minionData: MinionXPData[] = [];
    let cumulativeXP = 0;
    let currentLevel = 1;
    const maxLevelToShow = 6; // Show detailed minion breakdown up to level 6
    
    // Process waves until we reach level 6
    for (let waveNum = 1; waveNum <= 20 && currentLevel < (maxLevelToShow + 1); waveNum++) {
      const waveType = getWaveType(waveNum);
      const clearTimeSeconds = getWaveClearTime(waveNum); // When wave is cleared (XP earned)
      const killOrderForWave = getMinionKillOrder(waveType);
      
      // Track minion counts for descriptions
      const minionCounts = { melee: 0, caster: 0, cannon: 0 };
      
      // Process each minion in the wave according to kill order
      for (let minionIndex = 0; minionIndex < killOrderForWave.length; minionIndex++) {
        const minionType = killOrderForWave[minionIndex];
        let baseXP: number;
        let soloXP: number;
        
        if (minionType === 'melee') {
          baseXP = baseXPValues.melee;
          soloXP = actualXPValues.melee;
        } else if (minionType === 'caster') {
          baseXP = baseXPValues.caster;
          soloXP = actualXPValues.caster;
        } else {
          baseXP = baseXPValues.cannon;
          soloXP = actualXPValues.cannon;
        }
        
        const minionDescription = getMinionDescription(minionType, minionCounts);
        
        cumulativeXP += soloXP;
        
        // Check if this minion kill causes a level up
        let levelReached: number | undefined;
        if (currentLevel < levelData.length && cumulativeXP >= levelData[currentLevel].cumulativeXP) {
          currentLevel++;
          levelReached = currentLevel;
        }
        
        // Use wave clear time as timing reference (when XP is earned)
        const timing = formatTime(clearTimeSeconds);
        
        minionData.push({
          waveNumber: waveNum,
          minionType,
          minionIndex,
          minionDescription,
          baseXP,
          soloXP,
          cumulativeXP,
          levelReached,
          timing
        });
        
        // Stop if we've reached the display limit
        if (currentLevel >= (maxLevelToShow + 1)) break;
      }
    }
    
    return minionData;
  };

  const minionXPData = generateMinionXPData();

  // Get level up timings for first 6 levels
  const getLevelUpData = () => {
    const levelUps: { level: number; timing: string; minionType: string; minionDescription: string; wave: number }[] = [];
    
    minionXPData.forEach(minion => {
      if (minion.levelReached && minion.levelReached <= 6) {
        levelUps.push({
          level: minion.levelReached,
          timing: minion.timing,
          minionType: minion.minionType,
          minionDescription: minion.minionDescription,
          wave: minion.waveNumber
        });
      }
    });
    
    return levelUps;
  };

  const levelUpData = getLevelUpData();

  return (
    <div className="xp-calculator">
      <h5>XP Calculator</h5>
      
      {/* Settings */}
      <div className="xp-settings">
        <div className="setting-group">
          <label>Lane Type:</label>
          <select 
            value={laneType} 
            onChange={(e) => setLaneType(e.target.value as LaneType)}
            className="setting-dropdown"
          >
            <option value="solo">Solo Lane (95% XP)</option>
            <option value="duo">Duo Lane (62% XP)</option>
            <option value="trio">3-Champion (41.3% XP)</option>
            <option value="quad">4-Champion (31% XP)</option>
            <option value="penta">5-Champion (24.8% XP)</option>
          </select>
        </div>
        
        <div className="setting-group">
          <label>Kill Order:</label>
          <select 
            value={killOrder} 
            onChange={(e) => setKillOrder(e.target.value as KillOrder)}
            className="setting-dropdown"
          >
            <option value="melee-cannon-caster">Melee → Cannon → Caster</option>
            <option value="caster-cannon-melee">Caster → Cannon → Melee</option>
            <option value="melee-caster-cannon">Melee → Caster → Cannon</option>
          </select>
        </div>
      </div>
      
      {/* Level Up Timings */}
      <div className="level-up-timings">
        <h6>Level Up Timings ({laneType === 'solo' ? 'Solo Lane' : laneType === 'duo' ? 'Duo Lane' : `${laneType.charAt(0).toUpperCase() + laneType.slice(1)} Lane`})</h6>
        <div className="level-up-table">
          <div className="level-up-header">
            <span>Level</span>
            <span>Wave</span>
            <span>Wave Arrival</span>
            <span>Minion Kill</span>
        
            <span>XP Required</span>
          </div>
          {levelUpData.map(levelUp => {
            const levelInfo = levelData[levelUp.level - 1];
            return (
              <div key={levelUp.level} className="level-up-row">
                <span className="level-num">{levelUp.level}</span>
                <span className="wave-num">{levelUp.wave}</span>
                <span className="wave-arrival">{levelUp.timing}</span>
                <span className={`minion-type ${levelUp.minionType}`}>
                  {levelUp.minionType === 'melee' ? '⚔️' : levelUp.minionType === 'caster' ? '🏹' : '🔫'}
                  {levelUp.minionDescription}
                </span>
             
                <span className="xp-required">{levelInfo.cumulativeXP} XP</span>
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Detailed Minion XP Breakdown */}
      <div className="minion-xp-breakdown">
        <h6>Minion XP Breakdown</h6>
        <div className="xp-breakdown-table">
          <div className="xp-breakdown-header">
            <span>Wave</span>
            <span>Minion</span>
            <span>Type</span>
            <span>XP</span>
            <span>Cumulative</span>
            <span>Level</span>
          </div>
          {minionXPData.slice(0, 30).map((minion, idx) => (
            <div key={idx} className={`xp-breakdown-row ${minion.levelReached ? 'level-up' : ''}`}>
              <span className="wave-num">{minion.waveNumber}</span>
              <span className="minion-index">{minion.minionIndex + 1}</span>
              <span className={`minion-type ${minion.minionType}`}>
                {minion.minionType === 'melee' ? '⚔️' : minion.minionType === 'caster' ? '🏹' : '🔫'}
              </span>
              <span className="xp-value">{minion.soloXP}</span>
              <span className="cumulative-xp">{minion.cumulativeXP}</span>
              <span className="current-level">
                {minion.levelReached ? (
                  <span className="level-up-indicator">→ {minion.levelReached}</span>
                ) : (
                  <span className="current-level-num">
                    {levelData.findIndex(l => l.cumulativeXP > minion.cumulativeXP) || 1}
                  </span>
                )}
              </span>
            </div>
          ))}
        </div>
      </div>
      
      {/* XP Reference Table */}
      <div className="xp-reference">
        <h6>XP Reference</h6>
        <div className="xp-reference-table">
          <div className="xp-ref-header">
            <span>Level</span>
            <span>XP Required</span>
            <span>Cumulative XP</span>
            <span>Solo Waves</span>
            <span>Shared Waves</span>
          </div>
          {levelData.slice(1, showAllLevels ? undefined : 7).map(level => (
            <div key={level.level} className="xp-ref-row">
              <span className="level-num">{level.level}</span>
              <span className="xp-required">{level.xpRequired}</span>
              <span className="cumulative-xp">{level.cumulativeXP}</span>
              <span className="solo-waves">{level.soloWaves}</span>
              <span className="shared-waves">{level.sharedWaves}</span>
            </div>
          ))}
        </div>
        <div className="xp-reference-controls">
          <button 
            className="show-more-levels"
            onClick={() => setShowAllLevels(!showAllLevels)}
          >
            {showAllLevels ? 'Show Less Levels' : 'Show All Levels (1-20)'}
          </button>
        </div>
      </div>
      
      {/* Base XP Values Reference */}
      <div className="base-xp-reference">
        <h6>Minion XP Values</h6>
        <div className="base-xp-grid">
          <div className="xp-value-item">
            <span className="minion-icon">⚔️</span>
            <span className="minion-name">Melee</span>
            <span className="base-xp">Base: {baseXPValues.melee}</span>
            <span className="actual-xp">{laneType === 'solo' ? 'Solo' : laneType.charAt(0).toUpperCase() + laneType.slice(1)}: {actualXPValues.melee}</span>
          </div>
          <div className="xp-value-item">
            <span className="minion-icon">🏹</span>
            <span className="minion-name">Caster</span>
            <span className="base-xp">Base: {baseXPValues.caster}</span>
            <span className="actual-xp">{laneType === 'solo' ? 'Solo' : laneType.charAt(0).toUpperCase() + laneType.slice(1)}: {actualXPValues.caster}</span>
          </div>
          <div className="xp-value-item">
            <span className="minion-icon">🔫</span>
            <span className="minion-name">Cannon</span>
            <span className="base-xp">Base: {baseXPValues.cannon}</span>
            <span className="actual-xp">{laneType === 'solo' ? 'Solo' : laneType.charAt(0).toUpperCase() + laneType.slice(1)}: {actualXPValues.cannon}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default XPCalculator;
